import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import EditorToolRail from "../EditorToolRail";
import ContextualToolbar from "../ContextualToolbar";
import LeftDockPanels from "../LeftDockPanels";
import RightPropertiesPanel from "../RightPropertiesPanel";
import CanvasPagePreview from "../CanvasPagePreview";
import { useBookPages, SaveStatusBanner, CanvasToolPicker } from "../CanvasEditor";
import { api } from "../../../../lib/apiClient";
import {
  PanelLeftClose, PanelRightClose,
  ScrollText, BookOpen, Square as SquareIcon, Presentation,
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { useBookEditor } from "../BookEditorProvider";

const VIEW_MODES = [
  { id: "single", label: "Single Page", icon: SquareIcon },
  { id: "facing", label: "Facing Pages", icon: BookOpen },
  { id: "continuous", label: "Continuous", icon: ScrollText },
  { id: "presentation", label: "Presentation", icon: Presentation },
];

/** Canvas mode — reuses the existing page-object editor inside the unified shell. */
export default function CanvasMode() {
  const [params] = useSearchParams();
  const { bookId: providerBookId } = useBookEditor();
  const merged = new URLSearchParams(params);
  if (providerBookId && !merged.get("bookId")) merged.set("bookId", providerBookId);
  return <CanvasWorkspace params={merged} />;
}

function CanvasWorkspace({ params }) {
  const bookId = params.get("bookId");
  const {
    leftPanelOpen,
    setLeftPanelOpen,
    rightInspectorOpen,
    setRightInspectorOpen,
    toolSkillMode,
    setToolSkillMode,
  } = useBookEditor();

  const [book, setBook] = useState(null);
  const [pageImages, setPageImages] = useState({});
  const reloadAssets = useCallback(async () => {
    if (!bookId) return;
    try {
      const r = await api.get(`/api/book-lab/books/${bookId}/assets`);
      const map = {};
      for (const a of r.items || []) {
        if (a.page_id && a.status === "ready") {
          const m = /^page-(\d+)$/.exec(a.page_id);
          if (m) map[Number(m[1])] = a;
        }
      }
      setPageImages(map);
    } catch (_) { /* silent */ }
  }, [bookId]);

  useEffect(() => {
    if (!bookId) return;
    api.get(`/api/book-lab/books/${bookId}`).then(setBook).catch(() => {});
    reloadAssets();
    const onInsert = () => reloadAssets();
    window.addEventListener("book-lab:insert-image", onInsert);
    return () => window.removeEventListener("book-lab:insert-image", onInsert);
  }, [bookId, reloadAssets]);

  const title = book?.title || params.get("title") || "Untitled Book";
  const author = book?.author || params.get("author") || "Founder";
  const trim = params.get("trim") || "6x9";
  const pages = book ? (2 + (book.chapters?.length || 0)) : Number(params.get("pages") || 24);

  const [selectedTool, setSelectedTool] = useState("select");
  const [zoom, setZoom] = useState(100);
  const [fitMode, setFitMode] = useState("none");
  const [viewMode, setViewMode] = useState("continuous");
  const [startFirstPageOnRight, setStartFirstPageOnRight] = useState(true);

  const { pages: bookPages, reload: reloadPages, patchPage, applyOptimistic } = useBookPages(bookId);
  const [activeTool, setActiveTool] = useState("select");
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [editingObjectId, setEditingObjectId] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState(null);
  const pendingSaveRef = useRef(null);
  const saveTimerRef = useRef(null);

  const scheduleSave = useCallback((pageId, mut, opts = {}) => {
    const page = (bookPages || []).find((p) => p.id === pageId);
    if (!page) return;
    const nextObjects = mut(page.objects || []);
    applyOptimistic(pageId, nextObjects);
    pendingSaveRef.current = {
      pageId,
      objects: nextObjects,
      revision: page.revision,
      primary_content: opts.primary_content,
    };
    setSaveStatus("saving");
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const pending = pendingSaveRef.current;
      if (!pending) return;
      try {
        await patchPage(pending.pageId, {
          objects: pending.objects,
          revision: pending.revision,
        }, { primary_content: pending.primary_content });
        setSaveStatus("saved");
        setSaveError(null);
        setTimeout(() => setSaveStatus((s) => (s === "saved" ? "idle" : s)), 1400);
      } catch (e) {
        setSaveStatus("failed");
        setSaveError(e.message || "Save failed");
      }
    }, 550);
  }, [bookPages, patchPage, applyOptimistic]);

  const retrySave = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending) return;
    setSaveStatus("saving");
    try {
      await patchPage(pending.pageId, {
        objects: pending.objects,
        revision: pending.revision,
      }, { primary_content: pending.primary_content });
      setSaveStatus("saved");
      setSaveError(null);
      setTimeout(() => setSaveStatus((s) => (s === "saved" ? "idle" : s)), 1400);
    } catch (e) {
      setSaveStatus("failed");
      setSaveError(e.message);
    }
  }, [patchPage]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (editingObjectId) setEditingObjectId(null);
        else if (selectedObjectId) setSelectedObjectId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingObjectId, selectedObjectId]);

  const onSelectObject = (id) => { setSelectedObjectId(id); };
  const onEditObject = (id) => {
    setSelectedObjectId(id);
    setEditingObjectId(id);
  };
  const onCommitObjectText = (obj, newText) => {
    const page = (bookPages || []).find((p) => (p.objects || []).some((o) => o.id === obj.id));
    if (!page) return;
    if (obj.role === "primary-chapter-body") {
      scheduleSave(page.id, (arr) => arr, { primary_content: newText });
    } else {
      scheduleSave(page.id, (arr) => arr.map((o) => (o.id === obj.id ? { ...o, content: newText } : o)));
    }
  };
  const onCreateFrame = (pageId, rect) => {
    const id = `obj-${Math.random().toString(36).slice(2, 12)}`;
    scheduleSave(pageId, (arr) => ([
      ...arr,
      {
        id, type: "text", role: "decorative",
        x: rect.x, y: rect.y, width: rect.width, height: rect.height,
        rotation: 0, z_index: (arr.length ? Math.max(...arr.map((o) => o.z_index || 0)) : 0) + 1,
        locked: false, content: "New text",
        style: { fontFamily: "Manrope, sans-serif", fontSize: 12, color: "#071c1c" },
      },
    ]));
    setActiveTool("select");
    setSelectedObjectId(id);
    setEditingObjectId(id);
  };

  const [activePage, setActivePage] = useState(1);
  const scrollToPageRef = useRef(null);
  const onScrollToPageRef = useCallback((fn) => { scrollToPageRef.current = fn; }, []);
  const scrollToPage = useCallback((n) => {
    if (scrollToPageRef.current) scrollToPageRef.current(n);
  }, []);

  const handleZoomChange = (val) => {
    if (val === "page" || val === "spread") {
      setFitMode(val);
    } else {
      setFitMode("none");
      setZoom(Number(val));
    }
  };

  return (
    <div
      data-testid="book-editor-canvas-mode"
      className="flex flex-col h-full min-h-0 w-full overflow-hidden"
    >
      <div className="flex items-center border-b border-[var(--pl-border)] shrink-0">
        <div className="flex items-center gap-1 px-2 border-r border-[var(--pl-border)]">
          <PanelToggle
            testId="toggle-left-panel"
            active={leftPanelOpen}
            onClick={() => setLeftPanelOpen(!leftPanelOpen)}
            icon={PanelLeftClose}
            title="Toggle left panel"
          />
        </div>
        <div className="flex-1 min-w-0">
          <ContextualToolbar selectedTool={selectedTool} />
        </div>
        <div className="flex items-center gap-2 px-2 border-l border-[var(--pl-border)] shrink-0">
          <div
            data-testid="mode-toggle"
            className="inline-flex rounded-lg border border-[var(--pl-border)] overflow-hidden text-[10px] uppercase tracking-[0.18em]"
          >
            <button
              data-testid="mode-beginner"
              onClick={() => setToolSkillMode("beginner")}
              className={cn(
                "h-7 px-2.5",
                toolSkillMode === "beginner"
                  ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)]"
                  : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
              )}
            >
              Beginner
            </button>
            <button
              data-testid="mode-pro"
              onClick={() => setToolSkillMode("pro")}
              className={cn(
                "h-7 px-2.5",
                toolSkillMode === "pro"
                  ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)]"
                  : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
              )}
            >
              Pro
            </button>
          </div>
          <PanelToggle
            testId="toggle-right-panel"
            active={rightInspectorOpen}
            onClick={() => setRightInspectorOpen(!rightInspectorOpen)}
            icon={PanelRightClose}
            title="Toggle inspector"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex">
        <EditorToolRail
          mode={toolSkillMode}
          selectedTool={selectedTool}
          onSelect={setSelectedTool}
          collapsed={false}
        />
        <LeftDockPanels
          collapsed={!leftPanelOpen}
          pageCount={pages}
          activePage={activePage}
          onPageClick={scrollToPage}
        />
        <CanvasPagePreview
          title={title}
          author={author}
          trim={trim}
          zoom={zoom}
          viewMode={viewMode}
          pageCount={pages}
          startFirstPageOnRight={startFirstPageOnRight}
          fitMode={fitMode}
          onActivePageChange={setActivePage}
          onScrollToPageRef={onScrollToPageRef}
          pageImages={pageImages}
          pages={bookPages}
          selectedObjectId={selectedObjectId}
          editingObjectId={editingObjectId}
          onSelectObject={onSelectObject}
          onEditObject={onEditObject}
          onCommitObjectText={onCommitObjectText}
          onCreateFrame={onCreateFrame}
          activeTool={activeTool}
        />
        <RightPropertiesPanel
          collapsed={!rightInspectorOpen}
          book={book}
          bookId={bookId}
          onBookReload={async () => {
            if (bookId) {
              const b = await api.get(`/api/book-lab/books/${bookId}`);
              setBook(b);
              await reloadAssets();
              await reloadPages();
            }
          }}
        />
      </div>

      <div
        data-testid="canvas-mode-footer"
        className="h-8 border-t border-[var(--pl-border)] bg-[var(--pl-surface)]/80 backdrop-blur flex items-center gap-3 px-3 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)] shrink-0"
      >
        <div data-testid="view-mode-group" className="inline-flex rounded-md border border-[var(--pl-border)] overflow-hidden">
          {VIEW_MODES.map((v) => {
            const I = v.icon;
            const active = viewMode === v.id;
            return (
              <button
                key={v.id}
                data-testid={`view-mode-${v.id}`}
                onClick={() => setViewMode(v.id)}
                title={v.label}
                className={cn(
                  "h-6 px-2 inline-flex items-center gap-1",
                  active ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)]" : "hover:text-[var(--pl-text)]"
                )}
              >
                <I className="h-3 w-3" />
                <span className="hidden lg:inline">{v.label}</span>
              </button>
            );
          })}
        </div>

        <label
          data-testid="cover-rule-toggle"
          className={cn(
            "inline-flex items-center gap-1.5 cursor-pointer select-none",
            viewMode === "single" ? "opacity-40 cursor-not-allowed" : ""
          )}
          title="Start first page on right (print book cover rule)"
        >
          <input
            data-testid="cover-rule-input"
            type="checkbox"
            disabled={viewMode === "single"}
            checked={startFirstPageOnRight}
            onChange={(e) => setStartFirstPageOnRight(e.target.checked)}
            className="accent-[var(--pl-aqua)] h-3 w-3"
          />
          <span>First on right</span>
        </label>

        <div className="flex items-center gap-1">
          <span>Zoom</span>
          <select
            data-testid="zoom-select"
            value={fitMode !== "none" ? fitMode : String(zoom)}
            onChange={(e) => handleZoomChange(e.target.value)}
            style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
            className="h-5 rounded border border-[var(--pl-border)] px-1 text-[10px]"
          >
            <option value="page">Fit Page</option>
            <option value="spread">Fit Spread</option>
            {[50, 75, 100, 125, 150, 200].map((z) => (
              <option key={z} value={String(z)}>{z}%</option>
            ))}
          </select>
        </div>

        <span data-testid="status-active-page">Page {activePage} / {pages}</span>
        <span>Skill · {toolSkillMode}</span>
        <span className="hidden xl:inline">Tool · {selectedTool}</span>
      </div>

      <CanvasToolPicker activeTool={activeTool} onChange={setActiveTool} />
      <SaveStatusBanner status={saveStatus} error={saveError} onRetry={retrySave} />
    </div>
  );
}

function PanelToggle({ testId, icon: I, active, onClick, title }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={cn(
        "h-7 w-7 grid place-items-center rounded-md transition",
        active ? "text-[var(--pl-aqua)]" : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
      )}
    >
      <I className="h-3.5 w-3.5" />
    </button>
  );
}
